import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, MessageCircle, Mail } from "lucide-react";
import { useT } from "../lib/i18n";

const FAQ_NL = [
    {
        cat: "Bestellen",
        items: [
            { q: "Hoe lang duurt een bestelling?", a: "Standaard 5–7 werkdagen na bevestigde betaling. Voor spoed (≤3 werkdagen) kan je een spoedtoeslag betalen — contacteer ons voor de details." },
            { q: "Wat is de minimum bestelhoeveelheid?", a: "Geen minimum — je kan zelfs één t-shirt bestellen. Voor bulkprijzen vanaf 5 stuks krijg je automatisch staffelkorting (zie cart)." },
            { q: "Kan ik mijn bestelling nog wijzigen?", a: "Tot 2 uur na bestelling: stuur ons direct een mail of WhatsApp. Daarna is je ontwerp al doorgestuurd naar productie en kunnen we niet meer aanpassen." },
            { q: "Krijg ik een factuur?", a: "Ja, automatisch. Een PDF-factuur met je BTW-info wordt verstuurd na betalingsbevestiging." },
        ],
    },
    {
        cat: "Ontwerpen",
        items: [
            { q: "Wat zijn de printlimieten?", a: "Geen harde grenzen — je kan je ontwerp overal op het kledingstuk plaatsen. We tonen je visuele hulplijnen (middenlijn, oksellijn, kraagdaling) om alles mooi gecentreerd te houden." },
            { q: "Welke bestandsformaten kan ik uploaden?", a: "PNG, JPG, SVG en PDF werken het best. Voor scherpe prints adviseren we minimum 300 DPI of een vector-bestand." },
            { q: "Mag ik foto's met logo's of bekende personages uploaden?", a: "Enkel als jij de auteursrechten hebt. Voor logo's van clubs/bedrijven heb je toestemming nodig van de eigenaar." },
            { q: "Kan ik mijn ontwerp opslaan zonder direct te bestellen?", a: "Ja — als je ingelogd bent kan je je ontwerp opslaan via 'Bewaar design'. Zonder account wordt je werk lokaal in je browser bewaard tot je terugkomt." },
        ],
    },
    {
        cat: "Drukwerk & kwaliteit",
        items: [
            { q: "Welke druktechniek gebruiken jullie?", a: "DTF (Direct-to-Film) transfer — geschikt voor alle stoffen, met scherpe details en levendige kleuren. Ideaal voor zowel kleine logo's als grote prints." },
            { q: "Krimpen de t-shirts in de was?", a: "B&C t-shirts zijn voorgewassen. Normale wasinstructies: maximum 40°C, binnenstebuiten, geen droogkast voor de print. Volg de wasinstructies in het label." },
            { q: "Hoe lang gaat een print mee?", a: "Bij correct gewassen kledij minimum 50 wasbeurten zonder zichtbaar kwaliteitsverlies." },
        ],
    },
    {
        cat: "Verzending & retour",
        items: [
            { q: "Hoeveel kost verzending?", a: "België: €5. Nederland/Luxemburg: €8. Vanaf €75 totaal: GRATIS verzending." },
            { q: "Naar welke landen verzenden jullie?", a: "Standaard naar de Benelux. Voor andere EU-landen contacteer ons voor een offerte." },
            { q: "Kan ik retourneren?", a: "Standaard producten: 14 dagen retourrecht. Gepersonaliseerde ontwerpen kunnen NIET geretourneerd worden tenzij er een fout van onze kant in het drukwerk zit." },
            { q: "Wat als er een fout in mijn bestelling zit?", a: "Stuur ons binnen 7 dagen een foto via mail of WhatsApp — we lossen het direct op met een nieuwe gratis druk of volledige terugbetaling." },
        ],
    },
    {
        cat: "Betaling",
        items: [
            { q: "Welke betaalmethodes accepteren jullie?", a: "Op dit moment: betaling via SEPA QR-code (Bancontact-app, KBC, ING, Belfius, BNP Paribas Fortis) of klassieke bankoverschrijving." },
            { q: "Wanneer wordt mijn ontwerp in productie genomen?", a: "Zodra we de betaling ontvangen hebben. SEPA-overschrijvingen zijn meestal binnen 1 werkdag op onze rekening." },
            { q: "Is mijn betaling veilig?", a: "Ja — je betaalt rechtstreeks via je eigen bank-app. We bewaren geen kaartgegevens." },
        ],
    },
];

const FAQ_FR = [
    {
        cat: "Commander",
        items: [
            { q: "Combien de temps prend une commande ?", a: "Habituellement 5 à 7 jours ouvrés après confirmation du paiement. Pour les commandes urgentes (≤3 jours), un supplément s'applique — contactez-nous." },
            { q: "Quelle est la quantité minimum ?", a: "Aucun minimum — vous pouvez même commander un seul t-shirt. À partir de 5 pièces, une remise volume s'applique automatiquement." },
            { q: "Puis-je modifier ma commande ?", a: "Jusqu'à 2 heures après commande : envoyez-nous un mail ou un WhatsApp. Après cela, votre design est déjà transmis à la production." },
            { q: "Reçois-je une facture ?", a: "Oui, automatiquement. Une facture PDF est envoyée après confirmation du paiement." },
        ],
    },
    {
        cat: "Conception",
        items: [
            { q: "Quelles sont les limites d'impression ?", a: "Aucune limite stricte — vous pouvez placer votre design où vous voulez sur le vêtement. Des guides visuels vous aident à le centrer." },
            { q: "Quels formats puis-je télécharger ?", a: "PNG, JPG, SVG et PDF fonctionnent le mieux. Pour des impressions nettes, minimum 300 DPI ou un fichier vectoriel." },
            { q: "Puis-je utiliser des logos ou personnages connus ?", a: "Uniquement si vous détenez les droits. Pour les logos de clubs/entreprises, vous avez besoin de l'autorisation du propriétaire." },
            { q: "Puis-je sauvegarder mon design sans commander ?", a: "Oui — connecté, vous pouvez sauvegarder via 'Bewaar design'. Sans compte, votre travail est conservé localement dans votre navigateur." },
        ],
    },
    {
        cat: "Impression & qualité",
        items: [
            { q: "Quelle technique d'impression utilisez-vous ?", a: "Transfert DTF (Direct-to-Film) — adapté à tous les tissus, avec détails nets et couleurs vives." },
            { q: "Les t-shirts rétrécissent-ils au lavage ?", a: "Les t-shirts B&C sont pré-lavés. Lavez à 40°C max, retournés, sans sèche-linge pour la zone imprimée." },
            { q: "Combien de temps dure une impression ?", a: "Avec entretien correct : minimum 50 lavages sans perte de qualité visible." },
        ],
    },
    {
        cat: "Livraison & retour",
        items: [
            { q: "Combien coûte la livraison ?", a: "Belgique : 5€. Pays-Bas/Luxembourg : 8€. À partir de 75€ : livraison GRATUITE." },
            { q: "Vers quels pays expédiez-vous ?", a: "Benelux par défaut. Pour les autres pays de l'UE, contactez-nous pour un devis." },
            { q: "Puis-je retourner ?", a: "Articles standards : 14 jours. Les designs personnalisés ne peuvent PAS être retournés sauf défaut d'impression de notre part." },
            { q: "Et s'il y a une erreur dans ma commande ?", a: "Envoyez-nous une photo dans les 7 jours par mail ou WhatsApp — nous résoudrons cela par une nouvelle impression gratuite ou un remboursement total." },
        ],
    },
    {
        cat: "Paiement",
        items: [
            { q: "Quels moyens de paiement acceptez-vous ?", a: "Pour l'instant : paiement par QR code SEPA (Bancontact, KBC, ING, Belfius, BNP) ou virement bancaire classique." },
            { q: "Quand mon design est-il mis en production ?", a: "Dès réception du paiement. Les virements SEPA arrivent généralement sous 1 jour ouvré." },
            { q: "Mon paiement est-il sécurisé ?", a: "Oui — vous payez directement via votre app bancaire. Nous ne stockons aucune donnée de carte." },
        ],
    },
];

const FAQ_EN = [
    {
        cat: "Ordering",
        items: [
            { q: "How long does an order take?", a: "Standard 5–7 working days after confirmed payment. For rush orders (≤3 working days), an extra fee applies — please contact us." },
            { q: "What is the minimum order quantity?", a: "No minimum — you can order even one t-shirt. From 5 pieces, you automatically get volume discount." },
            { q: "Can I still change my order?", a: "Up to 2 hours after placing your order: send us a mail or WhatsApp. After that your design is already in production." },
            { q: "Do I get an invoice?", a: "Yes, automatically. A PDF invoice with your VAT info is sent after payment confirmation." },
        ],
    },
    {
        cat: "Designing",
        items: [
            { q: "What are the printing limits?", a: "No hard boundaries — you can place your design anywhere on the garment. We show visual guides (centerlines, armpit, collar drop) to keep it nicely centered." },
            { q: "Which file formats can I upload?", a: "PNG, JPG, SVG and PDF work best. For sharp prints we recommend minimum 300 DPI or a vector file." },
            { q: "Can I upload logos or known characters?", a: "Only if you own the rights. For club or company logos you need permission from the owner." },
            { q: "Can I save my design without ordering immediately?", a: "Yes — if logged in, you can save via 'Save design'. Without an account, your work is kept locally in your browser." },
        ],
    },
    {
        cat: "Printing & quality",
        items: [
            { q: "Which printing technique do you use?", a: "DTF (Direct-to-Film) transfer — suitable for all fabrics, with sharp details and vivid colors." },
            { q: "Do the t-shirts shrink in the wash?", a: "B&C t-shirts are pre-washed. Wash at max 40°C, inside-out, no tumble dry on printed area." },
            { q: "How long does a print last?", a: "With proper care: minimum 50 washes without visible quality loss." },
        ],
    },
    {
        cat: "Shipping & returns",
        items: [
            { q: "What does shipping cost?", a: "Belgium: €5. Netherlands/Luxembourg: €8. From €75 total: FREE shipping." },
            { q: "Which countries do you ship to?", a: "Benelux by default. For other EU countries, contact us for a quote." },
            { q: "Can I return?", a: "Standard products: 14-day return right. Personalised designs CANNOT be returned unless there is a printing error on our side." },
            { q: "What if there's an error in my order?", a: "Send us a photo within 7 days by mail or WhatsApp — we'll solve it directly with a free new print or full refund." },
        ],
    },
    {
        cat: "Payment",
        items: [
            { q: "Which payment methods do you accept?", a: "Currently: SEPA QR code payment (Bancontact, KBC, ING, Belfius, BNP) or classic bank transfer." },
            { q: "When does my design go into production?", a: "As soon as we receive the payment. SEPA transfers usually arrive within 1 working day." },
            { q: "Is my payment secure?", a: "Yes — you pay directly via your own banking app. We don't store any card details." },
        ],
    },
];

const SOURCES = { nl: FAQ_NL, fr: FAQ_FR, en: FAQ_EN };

function FaqItem({ item, idx }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={`faq-item-${idx}`} className="border-b border-zinc-200 last:border-b-0">
            <button
                onClick={() => setOpen(o => !o)}
                data-testid={`faq-toggle-${idx}`}
                className="w-full text-left py-5 flex items-start justify-between gap-4 group">
                <span className="font-display text-lg md:text-xl pr-4 group-hover:text-rose-600 transition-colors">{item.q}</span>
                <span className={`flex-shrink-0 w-7 h-7 border border-zinc-300 flex items-center justify-center mt-1 transition-all ${open ? "bg-zinc-950 text-white border-zinc-950 rotate-90" : ""}`}>
                    {open ? <Minus size={14} /> : <Plus size={14} />}
                </span>
            </button>
            {open && (
                <div className="pb-5 pr-12 text-sm text-zinc-600 leading-relaxed fade-in">
                    {item.a}
                </div>
            )}
        </div>
    );
}

export default function FaqPage() {
    const { t, lang } = useT();
    const data = SOURCES[lang] || SOURCES.nl;
    return (
        <div data-testid="faq-page" className="bg-zinc-50 min-h-screen">
            <section className="border-b border-zinc-200 bg-white">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
                    <p className="label-tiny">FAQ</p>
                    <h1 className="font-display text-5xl md:text-6xl mt-2 tracking-tight">{t("faq.title")}</h1>
                    <p className="text-zinc-600 mt-4 max-w-xl">{t("faq.sub")}</p>
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-6 lg:px-10 py-12 space-y-12">
                {data.map((section, s) => (
                    <div key={s} data-testid={`faq-cat-${s}`}>
                        <p className="label-tiny text-rose-600 mb-2">{`0${s + 1}`.slice(-2)} · {section.cat}</p>
                        <div className="border border-zinc-200 bg-white px-6">
                            {section.items.map((it, i) => (
                                <FaqItem key={i} item={it} idx={`${s}-${i}`} />
                            ))}
                        </div>
                    </div>
                ))}

                {/* CTA */}
                <div className="border border-zinc-200 bg-white p-6 mt-12">
                    <p className="label-tiny">Niet gevonden wat je zocht?</p>
                    <p className="font-display text-3xl mt-1">We helpen je graag verder.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <a href="https://wa.me/32491468280" target="_blank" rel="noopener noreferrer"
                            data-testid="faq-cta-whatsapp"
                            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe58] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors">
                            <MessageCircle size={14} /> WhatsApp
                        </a>
                        <Link to="/contact" data-testid="faq-cta-contact" className="btn-ghost inline-flex items-center gap-2">
                            <Mail size={14} /> Contact
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
